import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { NotificationServices, PublicNotificationDTO, WSServices } from "shared/services";
import { useAccount } from "../hooks/useAccount";

/**
 * Context unico para o sistema de notificacoes. Antes, useNotifications era
 * chamado independentemente em IconSidebar e NotificationsPanel — cada
 * instancia tinha seu proprio polling e state. Isso causava:
 *
 *  - markAllRead no Panel nao atualizava o badge da Sidebar (state desync)
 *  - Polling 2x (sidebar + panel) batia o servidor a cada 15s real (vs 30s
 *    configurado por instancia)
 *
 * Solucao: provider unico no topo da arvore (`<NotificationsProvider>`) que
 * faz o polling + mantem state, expondo via `useNotifications()` que retorna
 * sempre a mesma fonte de verdade. markRead/markAllRead atualizam state
 * global, sidebar e panel re-renderizam juntos.
 */

interface NotificationsContextValue {
	items: PublicNotificationDTO[];
	unreadCount: number;
	loading: boolean;
	error: string | null;
	fetchList: () => Promise<void>;
	refresh: () => Promise<void>;
	markRead: (id: string) => Promise<void>;
	markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

interface ProviderProps {
	children: React.ReactNode;
	intervalMs?: number;
}

export const NotificationsProvider: React.FC<ProviderProps> = ({ children, intervalMs = 30_000 }) => {
	const user = useAccount();
	const [items, setItems] = useState<PublicNotificationDTO[]>([]);
	const [unreadCount, setUnreadCount] = useState<number>(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const mountedRef = useRef(true);

	const fetchUnreadCount = useCallback(async () => {
		try {
			const count = await NotificationServices.unreadCount();
			if (mountedRef.current) setUnreadCount(count);
		} catch {
			// Silencia — polling nao deve poluir console
		}
	}, []);

	const fetchList = useCallback(async () => {
		setLoading(true);
		try {
			const list = await NotificationServices.list({ limit: 50 });
			if (mountedRef.current) {
				setItems(list);
				setError(null);
			}
		} catch (e: any) {
			if (mountedRef.current) setError(e?.message ?? "Erro ao carregar notificacoes");
		} finally {
			if (mountedRef.current) setLoading(false);
		}
	}, []);

	const refresh = useCallback(async () => {
		await Promise.all([fetchList(), fetchUnreadCount()]);
	}, [fetchList, fetchUnreadCount]);

	const markRead = useCallback(async (id: string) => {
		setItems((prev) => prev.map((n) => (n.id === id && !n.read ? { ...n, read: true, readAt: new Date().toISOString() } : n)));
		setUnreadCount((prev) => Math.max(prev - 1, 0));
		try {
			await NotificationServices.markRead(id);
		} catch {
			await refresh();
		}
	}, [refresh]);

	const markAllRead = useCallback(async () => {
		const nowIso = new Date().toISOString();
		setItems((prev) => prev.map((n) => (n.read ? n : { ...n, read: true, readAt: nowIso })));
		setUnreadCount(0);
		try {
			await NotificationServices.markAllRead();
		} catch {
			await refresh();
		}
	}, [refresh]);

	useEffect(() => {
		mountedRef.current = true;
		if (!user) {
			setItems([]);
			setUnreadCount(0);
			return;
		}

		void fetchUnreadCount();

		intervalRef.current = setInterval(() => {
			if (!document.hidden) void fetchUnreadCount();
		}, intervalMs);

		const onVisibility = () => {
			if (!document.hidden) void fetchUnreadCount();
		};
		const onFocus = () => void fetchUnreadCount();

		// EK-409 / real-time: backend NotificationService emite "notification:new"
		// no canal pessoal do user quando uma notif e persistida. Aqui aplicamos
		// upsert otimista no state local (sino atualiza sem esperar polling 30s).
		const wsSub = WSServices.onReceive<PublicNotificationDTO>("notification:new", (notif) => {
			if (!mountedRef.current) return;
			if (notif?.accountId && user?.uid && notif.accountId !== user.uid) return; // safety
			setItems((prev) => {
				if (prev.some((n) => n.id === notif.id)) return prev; // dedup
				return [notif, ...prev];
			});
			if (!notif.read) setUnreadCount((prev) => prev + 1);
		});

		document.addEventListener("visibilitychange", onVisibility);
		window.addEventListener("focus", onFocus);

		return () => {
			mountedRef.current = false;
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener("focus", onFocus);
			wsSub.stop();
		};
	}, [user, intervalMs, fetchUnreadCount]);

	const value: NotificationsContextValue = {
		items,
		unreadCount,
		loading,
		error,
		fetchList,
		refresh,
		markRead,
		markAllRead,
	};

	return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

/**
 * Consome o NotificationsContext. Se chamado fora do Provider, retorna noop
 * com state vazio — evita crash em testes/storybook e caminhos de erro.
 */
export const useNotifications = (): NotificationsContextValue => {
	const ctx = useContext(NotificationsContext);
	if (!ctx) {
		return {
			items: [],
			unreadCount: 0,
			loading: false,
			error: null,
			fetchList: async () => {},
			refresh: async () => {},
			markRead: async () => {},
			markAllRead: async () => {},
		};
	}
	return ctx;
};
